import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { RTL } from 'src/app/@theme/const';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { ApiGridInfo, ExportOptions, GridInfo, IActualSystemMenuPermission } from 'src/app/tabms/models/base/base';
import { BaseService } from 'src/app/tabms/services/base/base.service';
import { CommandService } from 'src/app/tabms/services/global/command.service';
import { FieldDictionaryListService } from 'src/app/tabms/services/global/field-dictionary-list.service';
import { FieldsConfigService } from 'src/app/tabms/services/global/fields-config.service';
import { PermissionsListService } from 'src/app/tabms/services/global/permissions-list.service';
import { UserFavouritePageService } from 'src/app/tabms/services/global/user-favourite-page.service';
import { TabSystemMenuService } from 'src/app/tabms/services/menu/tab-system-menu.service';
import { SessionWrapperService } from 'src/app/tabms/services/session/session-wrapper.service';
import { UserMenuService } from 'src/app/tabms/services/user/user-menu.service';
import { ADDTOFAV, OPERERROR, REMOVEVEF } from 'src/app/tabms/tab-consts';

@Component({
  selector: 'app-base-report-page-tool-bar',
  templateUrl: './base-report-page-tool-bar.component.html',
  styleUrl: './base-report-page-tool-bar.component.scss'
})
export class BaseReportPageToolBarComponent<T> implements OnInit, OnDestroy {

  userFavouritePageId: string;
  isFavourite = false;
  favColor = '#000000';
  userId: string;
  systemMenuId: string;
  exportOptions: ExportOptions[] = [];
  selectedOption: string = '';
  permissions: IActualSystemMenuPermission
  pageName: string;
  currentLang: string;
  pageNameTitle: string;
  menuKey: string;
  pageParameter: string;
  extraDynamicMenu

  private destroy$: Subject<void> = new Subject<void>();

  @Input() serviceInstance

  constructor(
    private baseService: BaseService,
    private userFavouritePageService: UserFavouritePageService,
    private activatedRoute: ActivatedRoute,
    private permissionListService: PermissionsListService,
    private fieldDictionaryList: FieldDictionaryListService,
    private themeLayoutService: ThemeLayoutService,
    private userMenuService: UserMenuService,
    private sessionService: SessionWrapperService,
    private tabSystemMenuService: TabSystemMenuService,
    private commandService: CommandService,
    private fieldsConfigService: FieldsConfigService<any>
  ) {
    this.currentLang = this.baseService.getCurrentLanguage();
    this.activatedRoute.paramMap.pipe(takeUntil(this.destroy$)).subscribe({
      next: (params) => {
        this.systemMenuId = params.get('id');
        this.fieldDictionaryList.getPageFields(this.systemMenuId);
        this.fieldsConfigService.setMenuId(this.systemMenuId);
        this.getGridInfoData();
        this.setMenuKey(this.systemMenuId);
        this.setPageName(this.systemMenuId);
      }
    });

    //#region lang Change
    this.themeLayoutService.directionChange.pipe(takeUntil(this.destroy$)).subscribe({
      next: (dir) => {
        (dir === RTL) ? this.currentLang = 'ar' : this.currentLang = 'en'
        this.getGridInfoData();
        this.setPageName(this.systemMenuId);
      }
    })
    //#endregion
  }

  ngOnInit(): void {
    this.getExtraDynamicMenu();

    //#region get Favourite Pages
    this.userFavouritePageService.getUserFavouritePages()?.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        let userFavPages = res.result;
        if (userFavPages) {
          this.userFavouritePageId = userFavPages.find((x) => x.tabsystemmenuid == this.systemMenuId)?.id;
          this.isFavourite = this.userFavouritePageId ? true : false;
          this.favColor = this.isFavourite ? '#ddcd0a' : '#000000';
        }
      },
      error: (err) => {
        console.log('userFavouritePageService.getbyParam err', err);
        this.isFavourite = false;
      }
    });
    //#endregion

    //#region get Page Permissions
    this.permissionListService.getPagePermissions().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.permissions = res;
        this.setOptions();
      }
    });
    //#endregion

    this.baseService.clearPageSelectedOption.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res) this.selectedOption = '';
      }
    })
  }

  //#region favourite
  favouriteClick() {
    if (this.isFavourite) {
      this.favColor = '#000000';
      this.removeFavourite();
    } else {
      this.favColor = '#ddcd0a';
      this.addFavourite();
    }
    this.isFavourite = !this.isFavourite;
  }

  addFavourite() {
    let claims = JSON.parse(sessionStorage.getItem('id_token_claims_obj'));
    if (claims) {
      this.userId = claims['sub'];
    }
    let userFavourite = {
      identityuserid: this.userId,
      tabsystemmenuid: this.systemMenuId,
      menukey: this.menuKey,
      pageparameter: this.pageParameter
    };
    this.userFavouritePageService.create(userFavourite)?.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.userFavouritePageId = res?.result?.id;
        this.baseService.showSuccessMessage(ADDTOFAV);
        this.baseService.addRemoveFavourite.next(1);
      },
      error: (err) => {
        console.log('userFavouritePage create err', err);
        this.baseService.showErrorMessage(OPERERROR);
      }
    });
  }

  removeFavourite() {
    this.userFavouritePageService.delete(this.userFavouritePageId)?.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.baseService.showSuccessMessage(REMOVEVEF);
        this.baseService.addRemoveFavourite.next(2);
      },
      error: (err) => {
        console.log('userFavouritePageService.delete err', err);
        this.baseService.showErrorMessage(OPERERROR);
      }
    });
  }
  //#endregion

  //#region Export Options
  setOptions() {
    if (this.permissions?.print && this.permissions?.export) {
      this.exportOptions = [
        { value: 'excel', viewValue: 'exportExcel' },
        { value: 'csv', viewValue: 'exportCSV' },
        { value: 'pdf', viewValue: 'exportPdf' }
      ];
    }
    else if (!this.permissions?.print && this.permissions?.export) {
      this.exportOptions = [];
    }
  }

  onOptionClicked(selectedOption) {
    this.baseService.exportOptionsChange.next(selectedOption);
  }
  //#endregion

  //#region dynamic methods
  getExtraDynamicMenu() {
    this.commandService.getAll().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.result && res.result.length > 0) {
          this.extraDynamicMenu = res.result.filter((x) => x.tabsystemmenuid == this.systemMenuId && x.type == 1);
        }
      },
      error: (err) => {
        console.log('getExtraDynamicMenu err', err);
      }
    });
  }
  //#endregion

  //#region field dictionary
  // getGridInfoData() {
  //   this.fieldDictionaryList
  //     .getList()
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((res: any) => {
  //       console.log(res);
  //       if (res) {
  //         const pageData = res.find((x) => x.pageId == this.systemMenuId);
  //         console.log("Found Page Data:", pageData);
  //         if (pageData) {
  //           this.getSortableFields(pageData);
  //           this.getPrintFields(pageData);
  //         }
  //       }
  //     });
  // }

  // setGridInfo(pageData): void {
  //   let gridData: GridInfo[] = [];
  //   if (!pageData) return

  //   const displayData = pageData.filter((el) => el.displayList);
  //   console.log('pageData', displayData);

  //   gridData.push({ columnDef: 'select', header: 'select', cell: '', fieldType: '' });
  //   displayData.forEach((row) => {
  //     // console.log('row', row)
  //     gridData.push({
  //       columnDef: row.fieldName,
  //       header: this.currentLang === 'ar' ?
  //         row.langValue.find(x => x.cultureName == 'ar-EG')?.value
  //         : row.langValue.find(x => x.cultureName == this.currentLang)?.value,
  //       cell: (element: T) => `${row.fieldName}`,
  //       fieldType: row.fieldType
  //     });
  //   });

  //   console.log('gridInfo', gridData);
  //   this.baseService.gridData.next(gridData);
  // }

  // getSortableFields(pageData) {
  //   if (!pageData) return
  //   const sortableFields = pageData.list.filter(el => el.sort !== undefined && el.sort !== null)
  //     .sort((a, b) => a.sort - b.sort);

  //   console.log('sortableFields', sortableFields);
  //   this.setGridInfo(sortableFields);
  // }

  // getPrintFields(pageData) {
  //   if (!pageData) return
  //   const printListFields = pageData.list.filter((el) => el.printable);
  //   this.baseService.printListFields.next(printListFields);
  // }
  //#endregion

  //#region get page grid columns
  getGridInfoData() {
    let gridData: ApiGridInfo[] = [];

    this.serviceInstance.gridList(this.menuKey, this.pageParameter, 1).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.headers && res.headers.length > 0) {
          gridData = res.headers;
          gridData.push({ fieldname: 'select', displayaraname: 'select', displaylatname: 'select', fieldtype: '' });
        }
      },
      error: (err) => {
        console.log('serviceInstance.gridList err', err);
      }
    })
    console.log('gridInfo', gridData);
    this.baseService.gridData.next(gridData);
  }
  //#endregion

  //#region get menu data
  setMenuKey(id) {
    this.tabSystemMenuService.get(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.menuKey = res.result.menukey;
        this.pageParameter = res.result.pageparameters;
        // console.log('this.menuKey', this.menuKey);
        this.sessionService.setMenuKeyAndPageParameter(this.menuKey, this.pageParameter);
      }
    });
  }

  setPageName(id) {
    this.userMenuService.get(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.pageName = res.result.menulatname.replaceAll(' ', '');
        this.sessionService.setPageName(this.pageName);
        this.pageNameTitle = this.currentLang === 'ar' ? res.result.menuaraname : res.result.menulatname;
        this.sessionService.setPageTitle(this.pageNameTitle);
      }
    });
  }
  //#endregion

  //#region destroy 
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  //#endregion
}
